import { Head, Link, router, useForm } from "@inertiajs/react";
import { ArrowLeft, Save } from "lucide-react";
import type { FormEvent } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppLayout from "@/layouts/app-layout";
import { newUserPath, usersPath } from "@/routes";
import type { BreadcrumbItem, User } from "@/types";

import type { UserGroupOption } from "./types";

interface UsersNewProps {
  user: Partial<User>;
  groups: UserGroupOption[];
}

export default function UsersNew({ user, groups }: UsersNewProps) {
  const { data, setData, processing, errors } = useForm({
    full_name: user.full_name ?? user.name ?? "",
    email: user.email ?? "",
    password: "",
    password_confirmation: "",
    group_ids: groups
      .filter((group) => group.assigned)
      .map((group) => group.id),
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Usuarios",
      href: usersPath(),
    },
    {
      title: "Nuevo usuario",
      href: newUserPath(),
    },
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    router.post(usersPath(), { user: data });
  };

  const toggleGroup = (groupId: number) => {
    const currentIds = Array.isArray(data.group_ids) ? data.group_ids : [];
    if (currentIds.includes(groupId)) {
      setData(
        "group_ids",
        currentIds.filter((id) => id !== groupId)
      );
    } else {
      setData("group_ids", [...currentIds, groupId]);
    }
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nuevo usuario" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={usersPath()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Nuevo usuario</h1>
            <p className="text-muted-foreground">
              Ingresa los datos del colaborador y asigna sus permisos.
            </p>
          </div>
        </div>

        <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle>Información del usuario</CardTitle>
            <CardDescription>
              Los campos marcados con * son obligatorios.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nombre completo *</Label>
                  <Input
                    id="full_name"
                    value={data.full_name}
                    onChange={(event) =>
                      setData("full_name", event.target.value)
                    }
                    placeholder="Nombre y apellido"
                    disabled={processing}
                  />
                  {errors.full_name && (
                    <p className="text-sm text-red-500">{errors.full_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.email}
                    onChange={(event) => setData("email", event.target.value)}
                    placeholder="usuario@empresa.com"
                    autoComplete="email"
                    disabled={processing}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={data.password}
                    onChange={(event) =>
                      setData("password", event.target.value)
                    }
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    disabled={processing}
                  />
                  {errors.password && (
                    <p className="text-sm text-red-500">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirmation">
                    Confirmar contraseña *
                  </Label>
                  <Input
                    id="password_confirmation"
                    type="password"
                    value={data.password_confirmation}
                    onChange={(event) =>
                      setData("password_confirmation", event.target.value)
                    }
                    placeholder="Repite la contraseña"
                    autoComplete="new-password"
                    disabled={processing}
                  />
                  {errors.password_confirmation && (
                    <p className="text-sm text-red-500">
                      {errors.password_confirmation}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Grupos y permisos</Label>
                <p className="text-sm text-muted-foreground">
                  Selecciona los grupos que determinan el nivel de acceso del
                  usuario.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {groups.map((group) => {
                    const groupIds = Array.isArray(data.group_ids)
                      ? data.group_ids
                      : [];
                    const checked = groupIds.includes(group.id);
                    return (
                      <label
                        key={group.id}
                        className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/10 p-3"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleGroup(group.id)}
                          disabled={processing}
                        />
                        <div>
                          <p className="text-sm font-medium leading-none">
                            {group.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {group.key}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                  {groups.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No hay grupos configurados.
                    </p>
                  )}
                </div>
                {errors.group_ids && (
                  <p className="text-sm text-red-500">{errors.group_ids}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={processing}>
                  <Save className="mr-2 h-4 w-4" />
                  {processing ? "Guardando..." : "Crear usuario"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  asChild
                  disabled={processing}
                >
                  <Link href={usersPath()}>Cancelar</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
